import { apiClient } from './client';

const AUTH_API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

/**
 * Organization statistics response from backend
 */
export interface OrganizationStatistics {
  staff: {
    totalPractitioners: number;
    activePractitioners: number;
    inactivePractitioners: number;
    bySpecialty: Record<string, number>;
    newHires: number;
    practitioners: Array<{
      id: string;
      name: string;
      role?: string;
      specialty?: string;
      status: 'active' | 'inactive';
      phone?: string;
      email?: string;
    }>;
  };
  patients: {
    totalPatients: number;
    newRegistrations: number;
    activePatients: number;
    patients: Array<{
      id: string;
      name: string;
      age?: number;
      gender?: string;
      phone?: string;
      registrationDate: string;
      lastVisit?: string;
    }>;
  };
  appointments: {
    total: number;
    booked: number;
    arrived: number;
    fulfilled: number;
    cancelled: number;
    noshow: number;
    byPractitioner: Record<
      string,
      {
        practitionerId: string;
        practitionerName: string;
        total: number;
        completed: number;
      }
    >;
    todayAppointments: Array<{
      id: string;
      patientName: string;
      time: string;
      practitionerName: string;
      status: string;
    }>;
  };
  consultations: {
    total: number;
    completed: number;
    inProgress: number;
    withAI: number;
    withoutAI: number;
    byPractitioner: Record<
      string,
      {
        practitionerId: string;
        practitionerName: string;
        total: number;
        withAI: number;
      }
    >;
    recentConsultations: Array<{
      id: string;
      patientName: string;
      practitionerName: string;
      date: string;
      status: string;
      withAI: boolean;
    }>;
  };
  dateRange: {
    start: string;
    end: string;
  };
  organizationId: string;
  lastUpdated: string;
}

/**
 * Statistics API response
 */
export interface StatisticsResponse {
  success: boolean;
  data?: OrganizationStatistics;
  error?: string;
}

/**
 * Statistics API client
 */
export const statisticsApi = {
  /**
   * Get organization statistics for a date range
   *
   * @param startDate - Start date in ISO 8601 format
   * @param endDate - End date in ISO 8601 format
   * @returns Organization statistics
   */
  getOrganizationStatistics: async (
    startDate: string,
    endDate: string
  ): Promise<OrganizationStatistics> => {
    const params = new URLSearchParams({ startDate, endDate });
    const { data } = await apiClient.get<StatisticsResponse>(
      `/v2/admin/statistics?${params.toString()}`,
      {
        baseUrl: AUTH_API_BASE_URL,
      }
    );

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to fetch organization statistics');
    }

    return data.data;
  },
};
